import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  Stack,
  Anchor,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconMailCheck } from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../../components/common';

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [sent, setSent] = useState(false);

  const form = useForm({
    initialValues: { email: '' },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: (values: { email: string }) => forgotPassword(values.email),
    // Always show success to prevent email enumeration
    onSuccess: () => setSent(true),
    onError: () => setSent(true),
  });

  const handleSubmit = (values: { email: string }) => {
    forgotPasswordMutation.mutate(values);
  };

  if (sent) {
    return (
      <Container size={420} my={40}>
        <Stack align="center" mb="lg">
          <Logo size="lg" />
        </Stack>

        <Paper withBorder shadow="md" p={30} radius="md">
          <Stack align="center" gap="md">
            <IconMailCheck size={48} color="var(--mantine-color-blue-6)" />
            <Title order={3} ta="center">
              Check Your Email
            </Title>
            <Text c="dimmed" size="sm" ta="center">
              If an account exists for {form.values.email}, we've sent password reset instructions.
              The link will expire in 1 hour.
            </Text>
            <Button component={Link} to="/login" fullWidth>
              Back to Login
            </Button>
            <Anchor
              size="sm"
              onClick={() => {
                setSent(false);
                form.reset();
              }}
            >
              Try a different email
            </Anchor>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size={420} my={40}>
      <Stack align="center" mb="lg">
        <Logo size="lg" />
        <Title order={2} ta="center">
          Forgot Password?
        </Title>
        <Text c="dimmed" size="sm" ta="center">
          Enter your email and we'll send you instructions to reset your password.
        </Text>
      </Stack>

      <Paper withBorder shadow="md" p={30} radius="md">
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Email"
              placeholder="you@example.com"
              required
              {...form.getInputProps('email')}
            />

            <Button type="submit" fullWidth loading={forgotPasswordMutation.isPending}>
              Send Reset Link
            </Button>
          </Stack>
        </form>

        <Text c="dimmed" size="sm" ta="center" mt="md">
          Remember your password?{' '}
          <Anchor component={Link} to="/login" size="sm">
            Log in
          </Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
